import type { TFunction } from 'i18next';
import { when } from 'mobx';

import type {
  BaseCampaign,
  Creative,
  DisplayCreative,
  IBannersnackUrl,
  TChannel,
} from '@feathr/blackbox';
import {
  CreativeClass,
  FacebookVideoCreative,
  getAspectRatioNotation,
  isAdTagCreative,
  isBannersnackCreative,
  metaAspectRatiosMatch,
  ValidDimensions,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import { toast } from '@feathr/components';

/**
 * The default ad tag HTML template for new ad tag creatives.
 */
export const DEFAULT_AD_TAG_HTML = `<ins style="display:inline-block;width:300px;height:250px;">
<a href="@FEATHR_CLICK@" target="_blank">
  <img alt="" src="data:image/png;base64,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" />
</a>
</ins>
@FEATHR_TRACK@`;

/**
 * Determines if a creative is a video creative.
 * @param creative - The Creative model
 * @returns True if the creative is a video type (DisplayVideo or FacebookVideo)
 */
export function isVideoCreative(creative: Creative): boolean {
  return (creative as DisplayCreative).isVideo;
}

/**
 * Determines if a creative is a Meta (Facebook) creative.
 * @param creative - The Creative model
 * @returns True if the creative is a Meta type (FacebookImage or FacebookVideo)
 */
export function isMetaCreative(creative: Creative): boolean {
  const cls = creative.get('_cls');
  return cls === CreativeClass.FacebookImage || cls === CreativeClass.FacebookVideo;
}

/**
 * Gets the dimensions string for a creative.
 * @param creative - The Creative model
 * @returns Dimensions string in format "widthxheight" or empty string if invalid
 */
export function getCreativeDimensions(creative: Creative): string {
  const width = creative.get('width', 0);
  const height = creative.get('height', 0);
  return width > 0 && height > 0 ? `${width}x${height}` : '';
}

/**
 * Gets the aspect ratio notation for a creative.
 * @param creative - The Creative model
 * @returns Aspect ratio string (e.g., "16:9") or empty string if invalid
 */
export function getCreativeAspectRatio(creative: Creative): string {
  const width = creative.get('width', 0);
  const height = creative.get('height', 0);
  if (width > 0 && height > 0) {
    return getAspectRatioNotation(width / height);
  }
  return '';
}

/**
 * Gets the URL hash for the creatives step in the campaign edit wizard.
 * @param campaign - The campaign model
 * @returns Hash string for the creatives step ("#step3" or "#step4")
 */
export function getCreativesStepHash(campaign: BaseCampaign): string {
  const { isFacebook, isOmniReachCampaign, isGeofencingCampaign } = campaign;
  const hideFiltersStep = isFacebook || isGeofencingCampaign || isOmniReachCampaign;
  return hideFiltersStep ? '#step3' : '#step4';
}

/**
 * Returns the allowed creative classes for a channel.
 * @param channel - The channel type ('display' or 'meta')
 * @returns Array of CreativeClass values
 */
export function getCreativeClassesForChannel(channel: TChannel): CreativeClass[] {
  if (channel === 'meta') {
    return [CreativeClass.FacebookImage, CreativeClass.FacebookVideo];
  }
  return [
    CreativeClass.DisplayImage,
    CreativeClass.DisplayVideo,
    CreativeClass.DisplayAdTag,
    CreativeClass.DisplayBannersnack,
  ];
}

/**
 * Creative info formatted for display in CreativeListItem.
 */
export interface ICreativeInfo {
  /** Spec name (e.g., "Medium Rectangle", "Square") */
  specName: string;
  /** Dimensions string (e.g., "300x250") */
  dimensions: string;
  /** Aspect ratio string (e.g., "6:5") */
  aspectRatio: string;
}

/**
 * Data structure for displaying a creative in list/card components.
 */
export interface ICreativeListItemData {
  /** Creative ID */
  id: string;
  /** URL for the creative thumbnail */
  thumbnailUrl: string;
  /** Creative name/title */
  title: string;
  /** Spec name (e.g., "Medium Rectangle", "Square") */
  specName: string;
  /** Dimensions string (e.g., "300x250") */
  dimensions: string;
  /** Aspect ratio string (e.g., "6:5") */
  aspectRatio: string;
  /** Whether the creative is a video */
  isVideo: boolean;
}

/**
 * Filters creatives by name using case-insensitive search.
 * @param creatives - Array of DisplayCreative models
 * @param query - Search query string
 * @returns Filtered array of creatives matching the query
 */
export function filterCreativesBySearch(
  creatives: DisplayCreative[],
  query: string,
): DisplayCreative[] {
  if (!query.trim()) {
    return creatives;
  }
  const lowerQuery = query.toLowerCase();
  return creatives.filter((creative) => {
    const name = creative.get('name') ?? '';
    return name.toLowerCase().includes(lowerQuery);
  });
}

/**
 * Gets the size name for creative dimensions by matching against valid dimension sets.
 * For Facebook creatives, matches by aspect ratio with tolerance.
 * @param width - Creative width in pixels
 * @param height - Creative height in pixels
 * @param cls - The creative class
 * @returns The size name (e.g., "Medium Rectangle", "Facebook Standard Square") or "Custom" if not found
 */
function getSizeName(width: number, height: number, cls?: CreativeClass): string {
  const key = `${width}x${height}`;

  if (cls === CreativeClass.FacebookImage) {
    const exactMatch = ValidFacebookDimensions[key];
    if (exactMatch) {
      return exactMatch.name;
    }
    const actualAspectRatio = width / height;
    const match = Object.values(ValidFacebookDimensions).find((dim) => {
      const specAspectRatio = dim.width / dim.height;
      return (
        metaAspectRatiosMatch(actualAspectRatio, specAspectRatio) &&
        width >= dim.width &&
        height >= dim.height
      );
    });
    return match?.name ?? 'Custom';
  }

  if (cls === CreativeClass.FacebookVideo) {
    const exactMatch = ValidFacebookVideoDimensions[key];
    if (exactMatch) {
      return exactMatch.name;
    }
    const actualAspectRatio = width / height;
    const match = Object.values(ValidFacebookVideoDimensions).find((dim) => {
      const specAspectRatio = dim.width / dim.height;
      return (
        metaAspectRatiosMatch(actualAspectRatio, specAspectRatio) &&
        width >= dim.width &&
        height >= dim.height
      );
    });
    return match?.name ?? 'Custom';
  }

  const dimension = ValidDimensions[key];
  return dimension?.name ?? 'Custom';
}

/**
 * Formats creative info for display in CreativeListItem.
 * @param creative - The DisplayCreative model
 * @returns Object with specName, dimensions, and aspectRatio
 */
export function formatCreativeInfo(creative: DisplayCreative): ICreativeInfo {
  const width = creative.get('width') ?? 0;
  const height = creative.get('height') ?? 0;
  const cls = creative.get('_cls');

  return {
    specName: getSizeName(width, height, cls),
    dimensions: creative.dimensions,
    aspectRatio: creative.aspectRatio,
  };
}

/**
 * Extracts a preview image URL from Bannersnack banner_urls.
 * Prefers jpg, then png, then any completed URL.
 *
 * @param bannerUrls - Array of banner URL objects from the creative
 * @returns The best available preview URL, or empty string if none
 */
export function getBannersnackPreviewUrl(bannerUrls: IBannersnackUrl[] | undefined): string {
  if (!bannerUrls?.length) {
    return '';
  }

  const doneUrls = bannerUrls.filter((u) => u.status === 'done');
  const jpg = doneUrls.find((u) => u.type === 'jpg');
  if (jpg) {
    return jpg.url;
  }

  const png = doneUrls.find((u) => u.type === 'png');
  if (png) {
    return png.url;
  }

  return doneUrls[0]?.url ?? '';
}

/**
 * Gets the thumbnail URL for any creative, handling Bannersnack creatives
 * which store their image in banner_urls instead of the url field.
 *
 * @param creative - The DisplayCreative model
 * @returns The best available thumbnail URL
 */
export function getCreativeThumbnailUrl(creative: DisplayCreative): string {
  if (isBannersnackCreative(creative)) {
    return getBannersnackPreviewUrl(creative.get('banner_urls'));
  }

  if (isAdTagCreative(creative)) {
    return creative.get('preview_url') ?? '';
  }

  if (creative instanceof FacebookVideoCreative) {
    return creative.get('ad_video_thumbnail') || creative.get('url') || '';
  }

  return creative.get('url') ?? '';
}

/**
 * Returns true when getCreativeThumbnailUrl returns a static image
 * for a video creative, so callers should skip video frame extraction.
 */
export function hasStoredThumbnail(creative: DisplayCreative): boolean {
  return creative instanceof FacebookVideoCreative && creative.hasStoredThumbnail;
}

/**
 * Waits for a BannersnackEditor unmount sync to complete.
 * The editor's cleanup effect calls model.sync() (fire-and-forget),
 * setting isUpdating to true then false. We use MobX `when` to
 * reactively wait for each transition instead of polling.
 * Timeout is non-fatal — proceeds with whatever state is available.
 *
 * @param creative - The creative model being synced
 */
export async function waitForBannersnackSync(creative: DisplayCreative): Promise<void> {
  try {
    await when(() => creative.isUpdating, { timeout: 10000 });
    await when(() => !creative.isUpdating, { timeout: 30000 });
  } catch {
    // Timeout — proceed with whatever state we have
  }
}

/**
 * Syncs a Bannersnack creative and validates its dimensions.
 * Shows an error toast if dimensions are invalid.
 *
 * @param creative - The creative to sync and validate
 * @param t - Translation function for error messages
 * @returns True if dimensions are valid, false otherwise
 */
export async function syncAndValidateBannersnackCreative(
  creative: DisplayCreative,
  t: TFunction,
): Promise<boolean> {
  await waitForBannersnackSync(creative);

  // Reload to get fresh banner_urls with 'done' status after sync completes
  await creative.reload();

  const dimensionKey = `${creative.get('width')}x${creative.get('height')}`;
  if (dimensionKey in ValidDimensions) {
    return true;
  }

  const validSizes = Object.keys(ValidDimensions).join(', ');
  toast(
    t('Invalid dimensions ({{dimensions}}). Please resize to a valid size: {{validSizes}}', {
      dimensions: dimensionKey,
      validSizes,
    }),
    { type: 'error' },
  );
  return false;
}

/**
 * Converts a DisplayCreative model to display data for CreativeListItem.
 * @param creative - The DisplayCreative model
 * @returns Object with all display properties needed for list items
 */
export function creativeToDisplayData(creative: DisplayCreative): ICreativeListItemData {
  const info = formatCreativeInfo(creative);
  return {
    id: creative.id,
    thumbnailUrl: getCreativeThumbnailUrl(creative),
    title: creative.get('name') ?? '',
    specName: info.specName,
    dimensions: info.dimensions,
    aspectRatio: info.aspectRatio,
    isVideo: isVideoCreative(creative),
  };
}
